import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  Briefcase, Plus, ChevronRight, Search, Building2, ArrowLeft, Languages,
  CheckCircle2, XCircle, FileText, X, CalendarRange,
} from "lucide-react";
import TransactionsTab from "@/pages/TransactionsTab";
import JournalTab from "@/pages/JournalTab";
import StatementsTab from "@/pages/StatementsTab";
import ReconciliationTab from "@/pages/ReconciliationTab";
import ReportsTab from "@/pages/ReportsTab";
import InvoicesTab from "@/pages/InvoicesTab";
import BillsTab from "@/pages/BillsTab";
import ClosingPacketModal from "@/components/bo/ClosingPacketModal";
import { useT, useLang } from "@/lib/i18n";

/* ------------------------------------------------------------------ */
/*  Lightweight modal (no shadcn Dialog dependency for portability)    */
/* ------------------------------------------------------------------ */
const Modal = ({ open, onClose, title, children, testid = "modal" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
      <div
        className="bg-white max-w-lg w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testid}-card`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
          <h3 className="font-serif-display text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]" data-testid={`${testid}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Books list view                                                    */
/* ------------------------------------------------------------------ */
const BooksList = ({ books, onOpen, onNew, query, setQuery }) => {
  const t = useT();
  const filtered = books.filter(
    (b) =>
      b.name.toLowerCase().includes(query.toLowerCase()) ||
      (b.client?.full_name || "").toLowerCase().includes(query.toLowerCase())
  );
  return (
    <div className="rn3-container py-12" data-testid="bo-books-list">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="rn3-eyebrow flex items-center gap-2"><Briefcase className="w-4 h-4" /> {t("admin.bo.eyebrow")}</div>
          <h1 className="font-serif-display text-5xl mt-3">{t("admin.bo.heading")}</h1>
          <p className="text-[#737373] text-sm mt-2">{t("admin.bo.subtitle")}</p>
        </div>
        <button onClick={onNew} className="rn3-btn-primary" data-testid="bo-new-book-btn">
          <Plus className="w-4 h-4" /> {t("admin.bo.new_book")}
        </button>
      </div>

      <div className="mt-8 flex items-center gap-3 border-b border-[#E5E5E5] pb-3">
        <Search className="w-4 h-4 text-[#737373]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("admin.bo.search_placeholder")}
          className="flex-1 bg-transparent outline-none text-sm"
          data-testid="bo-search-input"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {filtered.length === 0 && (
          <div className="text-sm text-[#737373] col-span-full">{t("admin.bo.empty")}</div>
        )}
        {filtered.map((b) => (
          <button
            key={b.id}
            onClick={() => onOpen(b)}
            data-testid={`book-card-${b.id}`}
            className="text-left rn3-card group flex flex-col"
          >
            <div className="flex items-start justify-between gap-3">
              <Building2 className="w-7 h-7 text-[#C4A252]" />
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[#FDFBF7] border border-[#E5E5E5] text-[#737373]">
                {b.entity_type.replace(/_/g, " ")}
              </span>
            </div>
            <h3 className="font-serif-display text-2xl mt-3 text-[#0A0A0A] group-hover:text-[#8B1C22] transition-colors">{b.name}</h3>
            <div className="border-t border-[#C4A252] w-8 mt-3 mb-3" />
            <div className="text-xs text-[#737373] space-y-1">
              {b.client?.full_name && <div>{t("admin.bo.client_label")}: <b className="text-[#0A0A0A]">{b.client.full_name}</b></div>}
              <div>{t("admin.bo.currency_label")}: {b.currency || "USD"}</div>
              <div>{b.account_count} {t("admin.bo.accounts_lower")} · {b.transaction_count} {t("admin.bo.txns_lower")}</div>
            </div>
            <div className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#8B1C22]">
              {t("admin.bo.open")} <ChevronRight className="w-3 h-3" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  New Book form                                                      */
/* ------------------------------------------------------------------ */
const NewBookForm = ({ entityTypes, onCancel, onCreated }) => {
  const t = useT();
  const { lang } = useLang();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    name: "",
    entity_type: "llc",
    client_user_id: "",
    fiscal_year_start_month: 1,
    currency: "USD",
    ein: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.client_user_id) payload.client_user_id = null;
      const { data } = await api.post("/back-office/books", payload);
      toast.success(t("admin.bo.toast.book_created"));
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin.bo.toast.create_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="new-book-form">
      <div>
        <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.book_name")}</label>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rn3-input" placeholder={t("admin.bo.field.book_name_placeholder")} data-testid="nb-name" />
      </div>
      <div className="grid grid-cols-2 gap-5">
        <div>
          <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.entity_type")}</label>
          <select value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className="rn3-input" data-testid="nb-entity">
            {entityTypes.map((e) => <option key={e.key} value={e.key}>{lang === "es" ? (e.label_es || e.label_en) : e.label_en}</option>)}
          </select>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.currency")}</label>
          <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="rn3-input" data-testid="nb-currency">
            <option>USD</option><option>MXN</option><option>EUR</option><option>CAD</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-5">
        <div>
          <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.fy_start")}</label>
          <select value={form.fiscal_year_start_month} onChange={(e) => setForm({ ...form, fiscal_year_start_month: Number(e.target.value) })} className="rn3-input" data-testid="nb-fy">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString(lang === "es" ? "es" : "en", { month: "long" })}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.ein")}</label>
          <input value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} className="rn3-input" placeholder="XX-XXXXXXX" data-testid="nb-ein" />
        </div>
      </div>
      <div>
        <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.link_client")}</label>
        <select value={form.client_user_id} onChange={(e) => setForm({ ...form, client_user_id: e.target.value })} className="rn3-input" data-testid="nb-client">
          <option value="">{t("admin.bo.field.not_linked")}</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name} · {c.email}</option>)}
        </select>
      </div>
      <div>
        <label className="rn3-eyebrow block mb-2">{t("admin.referrals.form.notes")}</label>
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rn3-input" rows="3" data-testid="nb-notes" />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="rn3-btn-gold" data-testid="nb-cancel">{t("common.cancel")}</button>
        <button type="submit" disabled={busy} className="rn3-btn-primary" data-testid="nb-submit">{busy ? t("admin.bo.creating") : t("admin.bo.create_book")}</button>
      </div>
    </form>
  );
};

/* ------------------------------------------------------------------ */
/*  Book detail (Chart of Accounts)                                    */
/* ------------------------------------------------------------------ */
const TYPE_COLORS = {
  asset: "border-[#C4A252] text-[#0A0A0A] bg-[#FDFBF7]",
  liability: "border-[#8B1C22] text-[#8B1C22] bg-white",
  equity: "border-[#0A0A0A] text-[#0A0A0A] bg-white",
  income: "border-[#15803D] text-[#15803D] bg-white",
  cogs: "border-[#737373] text-[#737373] bg-white",
  expense: "border-[#737373] text-[#404040] bg-white",
};

const BookDetail = ({ book, onBack, lang, setLang }) => {
  const t = useT();
  const [accounts, setAccounts] = useState([]);
  const [showNewAcc, setShowNewAcc] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("coa");
  const [showClosing, setShowClosing] = useState(false);
  const [bookState, setBookState] = useState(book);
  const [detailOptions, setDetailOptions] = useState({});
  const [bankTypes, setBankTypes] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/back-office/books/${book.id}/accounts`);
    setAccounts(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [book.id]);

  // One-shot fetch of the canonical detail-type vocabulary
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/back-office/account-detail-types`);
        if (cancelled) return;
        setDetailOptions(data?.detail_types || {});
        setBankTypes(data?.bank_types || []);
      } catch { /* silent — table still renders, just without dropdowns */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Publish the active book id so the Adelis chatbot can pull book-scoped
  // rules into its context.
  useEffect(() => {
    if (typeof window !== "undefined") window.__RN3_ACTIVE_BOOK_ID__ = book?.id || null;
    return () => { if (typeof window !== "undefined") window.__RN3_ACTIVE_BOOK_ID__ = null; };
  }, [book?.id]);

  const grouped = useMemo(() => {
    const filtered = accounts.filter(
      (a) =>
        (filter === "all" || a.type === filter) &&
        (a.code.includes(query) ||
          (a.name || "").toLowerCase().includes(query.toLowerCase()) ||
          (a.name_es || "").toLowerCase().includes(query.toLowerCase()))
    );
    const g = {};
    for (const a of filtered) {
      g[a.type] = g[a.type] || [];
      g[a.type].push(a);
    }
    return g;
  }, [accounts, filter, query]);

  const typeOrder = ["asset", "liability", "equity", "income", "cogs", "expense"];
  const typeLabel = {
    asset: { en: "Assets", es: "Activos" },
    liability: { en: "Liabilities", es: "Pasivos" },
    equity: { en: "Equity", es: "Capital" },
    income: { en: "Income", es: "Ingresos" },
    cogs: { en: "Cost of Goods Sold", es: "Costo de Bienes Vendidos" },
    expense: { en: "Expenses", es: "Gastos" },
  };

  const toggleActive = async (a) => {
    if (a.is_active) {
      await api.delete(`/back-office/books/${book.id}/accounts/${a.id}`);
    } else {
      await api.patch(`/back-office/books/${book.id}/accounts/${a.id}`, { is_active: true });
    }
    load();
  };

  const setDetailType = async (a, newVal) => {
    // Optimistic update so the badge flips instantly; roll back on failure.
    const prev = accounts;
    setAccounts((rows) => rows.map((r) => (r.id === a.id ? { ...r, detail_type: newVal } : r)));
    try {
      await api.patch(`/back-office/books/${book.id}/accounts/${a.id}`, { detail_type: newVal });
      toast.success(newVal ? `${a.code} → ${newVal}` : `${a.code} · type cleared`);
    } catch (err) {
      setAccounts(prev);
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const setNickname = async (a, newVal) => {
    const trimmed = (newVal || "").trim();
    if ((a.nickname || "") === trimmed) return;
    const prev = accounts;
    setAccounts((rows) => rows.map((r) => (r.id === a.id ? { ...r, nickname: trimmed } : r)));
    try {
      await api.patch(`/back-office/books/${book.id}/accounts/${a.id}`, { nickname: trimmed });
      toast.success(trimmed ? `${a.code} · nickname saved` : `${a.code} · nickname cleared`);
    } catch (err) {
      setAccounts(prev);
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="rn3-container py-10" data-testid="book-detail">
      <button onClick={onBack} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid="bo-back">
        <ArrowLeft className="w-3 h-3" /> {t("admin.bo.all_books")}
      </button>
      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="rn3-eyebrow">{book.entity_type.replace(/_/g, " ").toUpperCase()}</div>
          <h1 className="font-serif-display text-4xl mt-2">{book.name}</h1>
          {book.client?.full_name && <div className="text-sm text-[#737373] mt-1">{t("admin.bo.client_label")}: {book.client.full_name}</div>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowClosing(true)} className="rn3-btn-gold" data-testid="open-closing-packet" title={t("admin.bo.closing_packet_full")}>
            <CalendarRange className="w-4 h-4" /> {t("admin.bo.closing_packet")}
            {bookState.monthly_closing_enabled && <span className="ml-1 w-2 h-2 rounded-full bg-[#15803D]" data-testid="closing-packet-on-dot" />}
          </button>
          <button onClick={() => setLang(lang === "en" ? "es" : "en")} className="rn3-btn-gold" data-testid="lang-toggle">
            <Languages className="w-4 h-4" /> {lang === "en" ? "Español" : "English"}
          </button>
          {tab === "coa" && (
            <button onClick={() => setShowNewAcc(true)} className="rn3-btn-primary" data-testid="new-account-btn">
              <Plus className="w-4 h-4" /> {t("admin.bo.add_account")}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-[#E5E5E5] overflow-x-auto no-scrollbar" data-testid="bo-tabs">
        {[
          { key: "coa", label: lang==="es"?"Plan de Cuentas":"Chart of Accounts", active: true },
          { key: "txns", label: lang==="es"?"Transacciones":"Transactions", active: true },
          { key: "recon", label: lang==="es"?"Conciliación":"Reconciliation", active: true },
          { key: "reports", label: lang==="es"?"Reportes":"Reports", active: true },
          { key: "invoices", label: lang==="es"?"Facturas":"Invoices", active: true },
          { key: "vendors", label: lang==="es"?"Proveedores":"Vendors & Bills", active: true },
          { key: "journal", label: lang==="es"?"Asientos":"Journal Entries", active: true },
          { key: "statements", label: lang==="es"?"Estados de Cuenta":"Statements", active: true },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => t.active && setTab(t.key)}
            data-testid={`bo-tab-${t.key}`}
            disabled={!t.active}
            className={`px-4 py-3 text-xs uppercase tracking-widest whitespace-nowrap ${
              !t.active ? "text-[#737373] cursor-not-allowed" :
              tab === t.key ? "text-[#8B1C22] border-b-2 border-[#8B1C22] -mb-px" : "text-[#0A0A0A] hover:text-[#8B1C22]"
            }`}
          >
            {t.label} {!t.active && <span className="text-[9px] ml-1">soon</span>}
          </button>
        ))}
      </div>

      {tab === "txns" && (
        <div className="mt-8">
          <TransactionsTab book={book} accounts={accounts} lang={lang} />
        </div>
      )}

      {tab === "recon" && (
        <div className="mt-8">
          <ReconciliationTab book={book} accounts={accounts} lang={lang} />
        </div>
      )}

      {tab === "reports" && (
        <div className="mt-8">
          <ReportsTab book={book} accounts={accounts} lang={lang} />
        </div>
      )}

      {tab === "invoices" && (
        <div className="mt-8">
          <InvoicesTab book={book} accounts={accounts} lang={lang} />
        </div>
      )}

      {tab === "vendors" && (
        <div className="mt-8">
          <BillsTab book={book} accounts={accounts} lang={lang} />
        </div>
      )}

      {tab === "journal" && (
        <div className="mt-8">
          <JournalTab book={book} accounts={accounts} lang={lang} />
        </div>
      )}

      {tab === "statements" && (
        <div className="mt-8">
          <StatementsTab book={book} accounts={accounts} lang={lang} reloadAccounts={load} />
        </div>
      )}

      {tab === "coa" && (
      <>
      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="coa-filters">
        <div className="flex items-center gap-2 px-3 py-2 border border-[#E5E5E5]">
          <Search className="w-3.5 h-3.5 text-[#737373]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("admin.bo.search_account")} className="bg-transparent outline-none text-sm w-48" data-testid="coa-search" />
        </div>
        <div className="flex gap-1">
          {[["all", t("admin.bo.filter_all")], ...typeOrder.map((tp) => [tp, typeLabel[tp][lang]])].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`coa-filter-${k}`}
              className={`px-3 py-2 text-xs uppercase tracking-wider border ${filter===k?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-[#E5E5E5] hover:border-[#C4A252]"}`}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* Account groups */}
      <div className="mt-8 space-y-10" data-testid="coa-groups">
        {typeOrder.map((type) =>
          grouped[type]?.length ? (
            <section key={type} data-testid={`coa-group-${type}`}>
              <div className="flex items-baseline justify-between">
                <h2 className="font-serif-display text-2xl">{typeLabel[type][lang]}</h2>
                <span className="text-xs text-[#737373]">{grouped[type].length} {t("admin.bo.accounts_lower")}</span>
              </div>
              <div className="border-t border-[#C4A252] w-10 mt-2 mb-4" />
              <div className="border border-[#E5E5E5]">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
                    <tr>
                      <th className="py-2 px-3 w-24">{t("admin.bo.col.code")}</th>
                      <th className="py-2 px-3">{t("admin.col.name")}</th>
                      <th className="py-2 px-3 hidden md:table-cell">{t("admin.bo.col.subtype")}</th>
                      <th className="py-2 px-3 text-right w-32">{t("admin.bo.col.balance")}</th>
                      <th className="py-2 px-3 w-20">{t("admin.bo.col.active")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[type].map((a) => {
                      const opts = detailOptions[a.type] || [];
                      const isBank = a.detail_type && bankTypes.includes(a.detail_type);
                      const canNickname = !a.is_header && (a.type === "asset" || a.type === "liability");
                      return (
                      <tr key={a.id} className={`border-b border-[#F3EFE9] ${a.is_header ? "bg-[#FDFBF7] font-semibold" : ""} ${!a.is_active ? "opacity-50" : ""}`} data-testid={`acc-row-${a.id}`}>
                        <td className="py-2 px-3 tabular-nums text-[#737373]">{a.code}</td>
                        <td className="py-2 px-3">
                          <div className="text-[#0A0A0A] flex items-center gap-2 flex-wrap">
                            <span>{lang === "es" && a.name_es ? a.name_es : a.name}</span>
                            {a.nickname && (
                              <span
                                className="text-[11px] italic text-[#8B6914]"
                                data-testid={`acc-nickname-${a.id}`}
                                title="Nickname"
                              >
                                "{a.nickname}"
                              </span>
                            )}
                            {!a.is_header && a.detail_type && (
                              <span
                                className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
                                  isBank
                                    ? "border-[#15803D] text-[#15803D] bg-[#F0FDF4]"
                                    : "border-[#C4A252] text-[#8B6914] bg-[#FDFBF7]"
                                }`}
                                data-testid={`acc-detail-badge-${a.id}`}
                                title="Account type"
                              >
                                {a.detail_type}
                              </span>
                            )}
                          </div>
                          {canNickname && (
                            <input
                              type="text"
                              defaultValue={a.nickname || ""}
                              onBlur={(e) => setNickname(a, e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { e.currentTarget.value = a.nickname || ""; e.currentTarget.blur(); } }}
                              className="mt-1 text-[11px] w-full max-w-[280px] italic text-[#404040] border border-transparent hover:border-[#E5E5E5] focus:border-[#C4A252] focus:outline-none px-1 py-0.5 bg-transparent hover:bg-white focus:bg-white placeholder:not-italic placeholder:text-[#C4A252]"
                              placeholder={`+ Add nickname (e.g. "Dania's Personal Checking")`}
                              data-testid={`acc-nickname-input-${a.id}`}
                              maxLength={80}
                            />
                          )}
                          {!a.is_header && lang === "en" && a.name_es && <div className="text-xs text-[#737373]">{a.name_es}</div>}
                          {!a.is_header && lang === "es" && a.name && <div className="text-xs text-[#737373]">{a.name}</div>}
                        </td>
                        <td className="py-2 px-3 text-xs text-[#737373] hidden md:table-cell">
                          {a.is_header ? "" : (
                            opts.length > 0 ? (
                              <select
                                value={a.detail_type || ""}
                                onChange={(e) => setDetailType(a, e.target.value)}
                                className="text-xs border border-[#E5E5E5] px-2 py-1 bg-white hover:border-[#C4A252] focus:border-[#8B1C22] focus:outline-none min-w-[150px]"
                                data-testid={`acc-detail-select-${a.id}`}
                                title="Change account type"
                              >
                                <option value="">— None —</option>
                                {opts.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : (a.subtype && a.subtype !== "header" ? a.subtype.replace(/_/g, " ") : "")
                          )}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">${Number(a.balance || 0).toFixed(2)}</td>
                        <td className="py-2 px-3">
                          {a.is_header ? <span className="text-xs text-[#737373]">—</span> : (
                            <button onClick={() => toggleActive(a)} data-testid={`toggle-${a.id}`} className={`inline-flex items-center gap-1 text-xs ${a.is_active ? "text-[#15803D]" : "text-[#737373]"}`}>
                              {a.is_active ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            </button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null
        )}
        {Object.keys(grouped).length === 0 && (
          <div className="text-sm text-[#737373] text-center py-12">
            <FileText className="w-8 h-8 mx-auto mb-3 text-[#C4A252]" />
            {t("admin.bo.no_match")}
          </div>
        )}
      </div>
      </>
      )}

      <Modal open={showNewAcc} onClose={() => setShowNewAcc(false)} title={t("admin.bo.add_account")} testid="new-acc-modal">
        <NewAccountForm bookId={book.id} onCancel={() => setShowNewAcc(false)} onCreated={() => { setShowNewAcc(false); load(); }} />
      </Modal>
      <ClosingPacketModal open={showClosing} onClose={() => setShowClosing(false)} book={bookState} lang={lang} onUpdated={(b) => setBookState((prev) => ({ ...prev, ...b }))} />
    </div>
  );
};

const NewAccountForm = ({ bookId, onCancel, onCreated }) => {
  const t = useT();
  const [form, setForm] = useState({ code: "", name: "", name_es: "", type: "expense", subtype: "expense", detail_type: "", nickname: "", parent_code: "", is_header: false });
  const [busy, setBusy] = useState(false);
  const [detailOptions, setDetailOptions] = useState({});

  // Load the canonical detail-type vocabulary once per mount so the dropdown
  // stays in lockstep with the backend (which also owns Cash Flow / BS
  // detection via _cash_codes).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/back-office/account-detail-types`);
        if (!cancelled) setDetailOptions(data?.detail_types || {});
      } catch { /* graceful — form still works without detail_type */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reset detail_type when the account-type flips so we never leave a
  // Checking picked under an Expense account.
  const onTypeChange = (newType) => {
    const opts = detailOptions[newType] || [];
    setForm((f) => ({ ...f, type: newType, detail_type: opts[0] || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/back-office/books/${bookId}/accounts`, { ...form, parent_code: form.parent_code || null });
      toast.success(t("admin.bo.toast.account_added"));
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin.toast.failed"));
    } finally { setBusy(false); }
  };

  const detailOptsForType = detailOptions[form.type] || [];

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="new-acc-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">{t("admin.bo.col.code")}</label>
          <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rn3-input" placeholder="6200" data-testid="na-code" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.type")}</label>
          <select value={form.type} onChange={(e) => onTypeChange(e.target.value)} className="rn3-input" data-testid="na-type">
            <option value="asset">{t("admin.bo.account_type.asset")}</option>
            <option value="liability">{t("admin.bo.account_type.liability")}</option>
            <option value="equity">{t("admin.bo.account_type.equity")}</option>
            <option value="income">{t("admin.bo.account_type.income")}</option>
            <option value="cogs">{t("admin.bo.account_type.cogs")}</option>
            <option value="expense">{t("admin.bo.account_type.expense")}</option>
          </select>
        </div>
      </div>
      {detailOptsForType.length > 0 && (
        <div>
          <label className="rn3-eyebrow block mb-2">
            {form.type === "asset" || form.type === "liability"
              ? "Bank / Account Type"
              : "Detail Type"}
          </label>
          <select
            value={form.detail_type || ""}
            onChange={(e) => setForm({ ...form, detail_type: e.target.value })}
            className="rn3-input"
            data-testid="na-detail-type"
          >
            <option value="">— Select —</option>
            {detailOptsForType.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {(form.type === "asset" || form.type === "liability") && (
            <p className="text-xs text-[#737373] mt-1">
              Pick Checking, Savings, Credit Card, etc. so this account is labeled correctly across statements, reconciliation, and reports.
            </p>
          )}
        </div>
      )}
      {(form.type === "asset" || form.type === "liability") && (
        <div>
          <label className="rn3-eyebrow block mb-2">Nickname <span className="text-[#737373] normal-case tracking-normal">(optional)</span></label>
          <input
            value={form.nickname}
            onChange={(e) => setForm({ ...form, nickname: e.target.value })}
            className="rn3-input"
            placeholder="Dania's Personal Checking"
            data-testid="na-nickname"
          />
          <p className="text-xs text-[#737373] mt-1">A friendly label shown alongside the account code &amp; type across dropdowns, statements and reports.</p>
        </div>
      )}
      <div>
        <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.name_en")}</label>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rn3-input" data-testid="na-name" />
      </div>
      <div>
        <label className="rn3-eyebrow block mb-2">{t("admin.bo.field.name_es")}</label>
        <input value={form.name_es} onChange={(e) => setForm({ ...form, name_es: e.target.value })} className="rn3-input" data-testid="na-name-es" />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="rn3-btn-gold" data-testid="na-cancel">{t("common.cancel")}</button>
        <button type="submit" disabled={busy} className="rn3-btn-primary" data-testid="na-submit">{busy ? "…" : t("admin.bo.add_account")}</button>
      </div>
    </form>
  );
};

/* ------------------------------------------------------------------ */
/*  Top-level Back Office                                              */
/* ------------------------------------------------------------------ */
const BackOffice = () => {
  const t = useT();
  const { lang: globalLang } = useLang();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [books, setBooks] = useState([]);
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [entityTypes, setEntityTypes] = useState([]);
  const [query, setQuery] = useState("");
  const [lang, setLang] = useState(globalLang);
  useEffect(() => { setLang(globalLang); }, [globalLang]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/portal");
  }, [user, loading, nav]);

  const refresh = async () => {
    try {
      const [b, e] = await Promise.all([api.get("/back-office/books"), api.get("/back-office/entity-types")]);
      setBooks(b.data); setEntityTypes(e.data);
    } catch { /* */ }
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  if (!user || user.role !== "admin") return null;

  return (
    <div data-testid="back-office-root">
      {active ? (
        <BookDetail book={active} onBack={() => { setActive(null); refresh(); }} lang={lang} setLang={setLang} />
      ) : (
        <BooksList books={books} onOpen={setActive} onNew={() => setShowNew(true)} query={query} setQuery={setQuery} />
      )}

      <Modal open={showNew} onClose={() => setShowNew(false)} title={t("admin.bo.new_client_book")} testid="new-book-modal">
        <NewBookForm
          entityTypes={entityTypes}
          onCancel={() => setShowNew(false)}
          onCreated={(b) => { setShowNew(false); refresh(); setActive(b); }}
        />
      </Modal>
    </div>
  );
};

export default BackOffice;
